import React, { useState, useEffect } from "react";
import {
  Container, Box, Typography, Button, Grid, Card, CardContent,
  CardActions, CircularProgress, Chip, Alert, Snackbar, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Switch
} from "@mui/material";
import { api, useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom"; // 1. Import useLocation
import { Edit, Delete, People, Add, ContentCopy, OpenInNew, Link as LinkIcon, NavigateNext, NavigateBefore, Search } from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import { InputAdornment } from "@mui/material";

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function MyJobs() {
const location = useLocation(); // 2. Initialize location hook
const navigate = useNavigate();
const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const JOBS_PER_PAGE = 10;
  const FIFTEEN_DAYS_MS = 1296000000; // 15 days in milliseconds

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const [openEdit, setOpenEdit] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    company_name: "",
    location: "",
    salary: "",
    description: "",
    requirements: ""
  });
  // 3. EFFECT: Listen for navigation state from Dashboard
    useEffect(() => {
      if (location.state && location.state.activeTab !== undefined) {
        setTabValue(location.state.activeTab);
        setPage(1); // Reset pagination when switching tabs via dashboard
      }
    }, [location.state]);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // 1. Fetch Data
  useEffect(() => {
    async function fetchData() {
      try {
        const [jobsRes, appsRes] = await Promise.all([
          api.get("/admin/jobs"),
          api.get("/admin/applications").catch(() => ({ data: [] }))
        ]);
        const jobList = jobsRes.data.jobs || (Array.isArray(jobsRes.data) ? jobsRes.data : []);
        setJobs(jobList);
        setApplications(appsRes.data.applications || []);
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // 1. Update the Filter Logic (line ~76)
  const filteredJobs = jobs.filter((job) => {
    // Tab Filter
    const isActive = job.is_active === true || job.is_active === 1;
    let matchesTab = true;
    if (tabValue === 1) matchesTab = isActive;  // Active
    else if (tabValue === 2) matchesTab = !isActive; // Inactive

    if (!matchesTab) return false;

    // Search Filter
    if (debouncedSearchTerm.trim() === "") return true;

    const term = debouncedSearchTerm.toLowerCase().trim();
    const title = (job.title || "").toLowerCase();
    const company = (job.company_name || job.companyName || "").toLowerCase();
    const locationName = (job.location || "").toLowerCase();
    const description = (job.job_description || job.description || "").toLowerCase();

    return title.includes(term) ||
           company.includes(term) ||
           locationName.includes(term) ||
           description.includes(term);
  });

  // 3. Pagination Variables
  const totalPages = Math.ceil(filteredJobs.length / JOBS_PER_PAGE);
  const paginatedJobs = filteredJobs.slice(
    (page - 1) * JOBS_PER_PAGE,
    page * JOBS_PER_PAGE
  );

  // 4. Event Handlers
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(1);
  };

  const handleEditOpen = (job) => {
    setSelectedJob(job);
    setEditForm({
      title: job.title || "",
      company_name: job.company_name || job.companyName || "",
      location: job.location || "",
      salary: job.salary || "",
      description: job.description || job.job_description || "",
      requirements: Array.isArray(job.requirements) 
        ? job.requirements.join("\n") 
        : (job.requirements || "")
    });
    setOpenEdit(true);
  };

  const handleEditChange = (e) => {
    if (e && e.target) {
      const { name, value } = e.target;
      setEditForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleQuillChange = (value) => {
    setEditForm((prev) => ({ ...prev, description: value }));
  };

  const handleUpdateJob = async () => {
    if (!selectedJob) return;
    try {
      const payload = {
        ...editForm,
        requirements: typeof editForm.requirements === 'string'
          ? editForm.requirements.split("\n").filter(r => r.trim())
          : editForm.requirements
      };
      const response = await api.patch(`/admin/jobs/${selectedJob.id}`, payload);
      setJobs((prevJobs) =>
        prevJobs.map((job) =>
          job.id === selectedJob.id ? { ...job, ...response.data } : job
        )
      );
      setSnackbar({ open: true, message: "Job updated successfully", severity: "success" });
      setOpenEdit(false);
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to update job", severity: "error" });
    }
  };

  const handleDeleteJob = (id) => {
    if (!id) return;

    setJobToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteJob = async () => {
    if (!jobToDelete) return;

    try {
      await api.delete(`/admin/jobs/${jobToDelete}`);

      setJobs((prev) =>
          prev.filter((job) => job.id !== jobToDelete)
      );

      setSnackbar({
        open: true,
        message: "Job deleted successfully",
        severity: "success"
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to delete job",
        severity: "error"
      });
    } finally {
      setDeleteDialogOpen(false);
      setJobToDelete(null);
    }
  };

  const handleToggleStatus = async (job) => {
    try {
      const newStatus = !job.is_active;

      await api.patch(`/admin/jobs/${job.id}`, {
        is_active: newStatus,
      });

      setJobs((prevJobs) =>
        prevJobs.map((j) =>
          j.id === job.id
            ? { ...j, is_active: newStatus }
            : j
        )
      );

      setSnackbar({
        open: true,
        message: `Job marked as ${
          newStatus ? "Active" : "Inactive"
        }`,
        severity: "success",
      });
    } catch (error) {
      console.error(error);

      setSnackbar({
        open: true,
        message: "Failed to update job status",
        severity: "error",
      });
    }
  };

  const handleCopyLink = async (job) => {
    try {
      let url = job.application_url;
      if (!url) {
        const res = await api.get(`/jobs/${job.id}/application-link`);
        url = res.data.applicationUrl;
        setJobs(prev => prev.map(j => j.id === job.id ? { ...j, application_url: url, application_slug: res.data.applicationSlug } : j));
      }
      await navigator.clipboard.writeText(url);
      setSnackbar({ open: true, message: "Application link copied!", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to copy link", severity: "error" });
    }
  };

  const handleOpenLink = async (job) => {
    try {
      let url = job.application_url;
      if (!url) {
        const res = await api.get(`/jobs/${job.id}/application-link`);
        url = res.data.applicationUrl;
        setJobs(prev => prev.map(j => j.id === job.id ? { ...j, application_url: url, application_slug: res.data.applicationSlug } : j));
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setSnackbar({ open: true, message: "Failed to open link", severity: "error" });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2, minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: "flex-start", mb: 2 }}>
        <Box>
          <Typography
            variant="h1"
            sx={{
              color: "#1E3A8A",
              fontWeight: "bold",
              mb: 0.5,
            }}
          >
            Jobs Posted
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#64748B",
              fontSize: "1rem",
            }}
          >
            Manage and track all your published job postings.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search jobs..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: { xs: '100%', sm: 280, md: 350 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: 'white',
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e0' },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#a0aec0', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={user?.plan === 'free_trial' && (user?.job_limit ?? 1) <= 0 ? 'Free Trial job posting limit reached. Please upgrade your plan.' : ''}>
            <span>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate("/admin/jobs/new")}
                disabled={user?.plan === 'free_trial' && (user?.job_limit ?? 1) <= 0}
                sx={{
                  bgcolor: "#1E3A8A",
                  '&:hover': { bgcolor: "#007a7a" },
                  textTransform: 'none',
                  fontWeight: 'bold',
                  borderRadius: '8px'
                }}
              >
                Post New Job
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
          <Tab label={`All (${jobs.length})`} sx={{ textTransform: 'none', fontWeight: 'bold' }} />
          <Tab label={`Active (${jobs.filter(j => j.is_active === true || j.is_active === 1).length})`} sx={{ textTransform: 'none', fontWeight: 'bold' }} />
          <Tab label={`Inactive (${jobs.filter(j => j.is_active === false || j.is_active === 0).length})`} sx={{ textTransform: 'none', fontWeight: 'bold' }} />
        </Tabs>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid transparent",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            border: "1px solid #1E3A8A",
            boxShadow: "0 8px 20px rgba(30,58,138,0.12)",
          },
          "&:focus-within": {
            border: "1px solid #1E3A8A",
            boxShadow: "0 8px 20px rgba(30,58,138,0.12)",
          },
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="jobs table">
          <TableHead sx={{ bgcolor: '#f8f9fa' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Job Title</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Salary</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Apply Link</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedJobs.length > 0 ? (
              paginatedJobs.map((job) => {
                const isActive = job.is_active === true || job.is_active === 1;
                return (
                  <TableRow key={job.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'top' }}>
                      {job.title}
                    </TableCell>
                    <TableCell sx={{ color: '#1E3A8A', verticalAlign: 'top' }}>
                      {job.company_name || job.companyName}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {job.location || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {job.salary || job.salary_range || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, verticalAlign: "top" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#334155",
                          fontWeight: 500,
                          lineHeight: 1.6,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          wordBreak: "break-word",
                        }}
                      >
                        {(job.job_description || job.description || "No description provided.").replace(/<[^>]*>?/gm, " ")}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Switch
                          checked={isActive}
                          onChange={() => handleToggleStatus(job)}
                          color="success"
                        />

                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isActive ? "#2e7d32" : "#d32f2f",
                          }}
                        >
                          {isActive ? "Active" : "Inactive"}
                        </Typography>
                      </Box>
                    </TableCell>
                    {/* Apply Link column */}
                    <TableCell sx={{ verticalAlign: 'top', textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                        <Tooltip title="Copy apply link">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyLink(job)}
                            sx={{ color: '#1E3A8A' }}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open apply link">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenLink(job)}
                            sx={{ color: '#1E3A8A' }}
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {job.application_slug && (
                        <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 0.5, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {job.application_slug}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<People />}
                          onClick={() => navigate(`/admin/jobs/${job.id}/candidates`)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 'bold',
                            color: '#1E3A8A',
                            borderColor: '#1E3A8A',
                            '&:hover': { borderColor: '#1E40AF', bgcolor: 'rgba(30,58,138,0.04)' }
                          }}
                        >
                          Screen Candidates
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => handleEditOpen(job)}
                          sx={{ color: '#1E3A8A' }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteJob(job.id)}
                          sx={{ color: '#ef4444' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                  <Typography variant="h6" color="textSecondary">
                    {searchTerm ? "No jobs found matching your search." : "No jobs found."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{
        mt: 4,
        mb: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 3,
        px: 1
      }}>
        <Typography variant="body2" sx={{ color: "#94A3B8", fontWeight: 500 }}>
          {filteredJobs.length === 0
            ? "Showing 0 to 0 of 0 jobs"
            : `Showing ${(page - 1) * JOBS_PER_PAGE + 1} to ${Math.min(
                page * JOBS_PER_PAGE,
                filteredJobs.length
              )} of ${filteredJobs.length} jobs`}
        </Typography>

        {totalPages > 0 && (
          <Box sx={{ display: "flex", gap: 1.5, alignItems: 'center' }}>
            <Button
              size="small"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              sx={{
                minWidth: 32,
                height: 32,
                p: 0,
                borderRadius: "50%",
                color: "#94A3B8",
                transition: "all 0.2s",
                "&:hover": { 
                  bgcolor: "rgba(30, 58, 138, 0.08)",
                  color: "#1E3A8A"
                },
                "&.Mui-disabled": { 
                  color: "#E2E8F0" 
                }
              }}
            >
              <NavigateBefore fontSize="small" />
            </Button>

            <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;
                const isActive = page === pageNum;
                return (
                  <Button
                    key={pageNum}
                    size="small"
                    onClick={() => setPage(pageNum)}
                    sx={{
                      minWidth: 32,
                      height: 32,
                      p: 0,
                      borderRadius: "50%",
                      bgcolor: isActive ? "#1E3A8A" : "transparent",
                      color: isActive ? "white" : "#94A3B8",
                      fontWeight: isActive ? 700 : 500,
                      fontSize: "0.875rem",
                      transition: "all 0.2s",
                      "&:hover": {
                        bgcolor: isActive ? "#1E40AF" : "rgba(30, 58, 138, 0.08)",
                        color: isActive ? "white" : "#1E3A8A",
                      }
                    }}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </Box>

            <Button
              size="small"
              disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage((p) => p + 1)}
              sx={{
                minWidth: 32,
                height: 32,
                p: 0,
                borderRadius: "50%",
                color: "#94A3B8",
                transition: "all 0.2s",
                "&:hover": { 
                  bgcolor: "rgba(30, 58, 138, 0.08)",
                  color: "#1E3A8A"
                },
                "&.Mui-disabled": { 
                  color: "#E2E8F0" 
                }
              }}
            >
              <NavigateNext fontSize="small" />
            </Button>
          </Box>
        )}
      </Box>

      {/* Edit Dialog with all form fields */}
            <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
              <DialogTitle>Edit Job Details</DialogTitle>
              <DialogContent dividers>
                <TextField margin="dense" label="Job Title" name="title" fullWidth value={editForm.title} onChange={handleEditChange} inputProps={{ 'data-allow-paste': 'true' }} />
                <TextField margin="dense" label="Company Name" name="company_name" fullWidth value={editForm.company_name} onChange={handleEditChange} inputProps={{ 'data-allow-paste': 'true' }} />
                <TextField margin="dense" label="Location" name="location" fullWidth value={editForm.location} onChange={handleEditChange} inputProps={{ 'data-allow-paste': 'true' }} />
                <TextField margin="dense" label="Salary Range" name="salary" fullWidth value={editForm.salary} onChange={handleEditChange} inputProps={{ 'data-allow-paste': 'true' }} />
                <Box sx={{ mt: 2, mb: 1 }} data-allow-paste="true">
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Job Description</Typography>
                  <ReactQuill
                    theme="snow"
                    value={editForm.description}
                    onChange={handleQuillChange}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        ['clean']
                      ],
                    }}
                    style={{ height: '200px', marginBottom: '50px' }}
                  />
                </Box>
                <TextField margin="dense" label="Requirements" name="requirements" fullWidth multiline rows={3} value={editForm.requirements} onChange={handleEditChange} inputProps={{ 'data-allow-paste': 'true' }} sx={{ '& .MuiInputBase-input': { whiteSpace: 'pre-line', wordBreak: 'break-word' } }} />
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
                <Button variant="contained" onClick={handleUpdateJob}>Save Changes</Button>
              </DialogActions>
            </Dialog>
      <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="xs"
          fullWidth
      >
        <DialogTitle>
          Delete Job
        </DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete this job?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
              onClick={() => {
                setDeleteDialogOpen(false);
                setJobToDelete(null);
              }}
          >
            Cancel
          </Button>

          <Button
              variant="contained"
              color="error"
              onClick={confirmDeleteJob}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
          </Container>
        );
      }

      export default MyJobs;
